import React, { useState, createContext, useContext } from 'react';
export interface Transaction {
  id: string;
  type: 'rental' | 'sale';
  date: string;
  transactionValue: number;
  agreedCommission: number;
  commissionedParty: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  propertyAddress: string;
  propertyDetails: string;
  notes?: string;
  status: 'in-preparation' | 'sent-to-billing' | 'billed';
  sellerCommission?: number;
  buyerCommission?: number;
  landlordCommission?: number;
  tenantCommission?: number;
  sellerVAT?: number;
  buyerVAT?: number;
  landlordVAT?: number;
  tenantVAT?: number;
  sellerTotal?: number;
  buyerTotal?: number;
  landlordTotal?: number;
  tenantTotal?: number;
  vatOption?: string;
  sellerVATOption?: string;
  buyerVATOption?: string;
  landlordVATOption?: string;
  tenantVATOption?: string;
  agentName?: string;
  transactionCode?: string;
  ownerType?: 'PF' | 'PJ';
  ownerName?: string;
  ownerPhone?: string;
  ownerCNP?: string;
  ownerCI?: string;
  ownerAddress?: string;
  ownerCUI?: string;
  ownerCompanyName?: string;
  ownerCompanyAddress?: string;
  clientType?: 'PF' | 'PJ';
  clientCNP?: string;
  clientCI?: string;
  clientAddress?: string;
  clientCUI?: string;
  clientCompanyName?: string;
  clientCompanyAddress?: string;
  ownerContract?: string;
  clientContract?: string;
}
interface TransactionContextType {
  transactions: Transaction[];
  addTransaction: (transaction: Omit<Transaction, 'id' | 'date' | 'status'>) => void;
  updateTransaction: (id: string, updates: Partial<Transaction>) => void;
  updateTransactionStatus: (id: string, status: Transaction['status']) => void;
  getTransactionById: (id: string) => Transaction | undefined;
  getMonthlyStats: () => {
    count: number;
    totalCommission: number;
  };
}
const TransactionContext = createContext<TransactionContextType | undefined>(undefined);
export function TransactionProvider({
  children
}: {
  children: ReactNode;
}) {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const addTransaction = (transaction: Omit<Transaction, 'id' | 'date' | 'status'>) => {
    const newTransaction: Transaction = {
      ...transaction,
      id: `TRX-${Date.now()}`,
      date: new Date().toISOString(),
      status: 'sent-to-billing'
    };
    setTransactions(prev => [newTransaction, ...prev]);
  };
  const updateTransaction = (id: string, updates: Partial<Transaction>) => {
    setTransactions(prev => prev.map(t => t.id === id ? {
      ...t,
      ...updates
    } : t));
  };
  const updateTransactionStatus = (id: string, status: Transaction['status']) => {
    setTransactions(prev => prev.map(t => t.id === id ? {
      ...t,
      status
    } : t));
  };
  const getTransactionById = (id: string) => {
    return transactions.find(t => t.id === id);
  };
  const getMonthlyStats = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const monthlyTransactions = transactions.filter(t => {
      const transactionDate = new Date(t.date);
      return transactionDate.getMonth() === currentMonth && transactionDate.getFullYear() === currentYear;
    });
    const totalCommission = monthlyTransactions.reduce((sum, t) => sum + t.agreedCommission, 0);
    return {
      count: monthlyTransactions.length,
      totalCommission
    };
  };
  return <TransactionContext.Provider value={{
    transactions,
    addTransaction,
    updateTransaction,
    updateTransactionStatus,
    getTransactionById,
    getMonthlyStats
  }} data-id="element-7103">
      {children}
    </TransactionContext.Provider>;
}
export function useTransactions() {
  const context = useContext(TransactionContext);
  if (!context) {
    throw new Error('useTransactions must be used within TransactionProvider');
  }
  return context;
}